import { PageContainer } from '@/components/PageContainer';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Select } from '@televet/kibble-ui/Select';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Modal } from '@televet/kibble-ui/Modal';
import { Flex, Table, Box, VStack } from '@televet/kibble-ui/chakra';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/eval-runs/')({
  component: RouteComponent,
  beforeLoad: () => ({
    pageTitle: 'Agent Evals',
  }),
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [runName, setRunName] = useState('');
  const [createError, setCreateError] = useState('');

  const { data: runsData } = useQuery(
    trpc.agentEvalRun.listRuns.queryOptions({ page, limit: 20 })
  );

  const { data: agentsData } = useQuery(
    trpc.agent.list.queryOptions({ page: 1, limit: 100 })
  );

  const { mutate: createRun, isPending: isCreating } = useMutation(
    trpc.agentEvalRun.createRun.mutationOptions({
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [['agentEvalRun', 'listRuns']],
        });
        setCreateDialogOpen(false);
        setSelectedAgentId('');
        setRunName('');
        setCreateError('');
        navigate({ to: `/eval-runs/${data.evalRun.id}` });
      },
      onError: (error) => {
        setCreateError(error.message);
      },
    })
  );

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Flex justify="space-between" align="center">
          <Heading size="lg">Agent Evals</Heading>
          <Button
            variant="primary"
            onClick={() => setCreateDialogOpen(true)}
            iconName="plus"
          >
            New Agent Eval
          </Button>
        </Flex>

        <Flex flex="1 1 auto" justify="center" align="flex-start">
          <Card
            flex="1 1 auto"
            direction="column"
            borderColor="border.default"
            rounded="lg"
            overflow="hidden"
          >
              <Box overflowX="auto" position="relative" width="100%">
                <Table.Root size="md" interactive width="100%" minWidth="700px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Name</Table.ColumnHeader>
                      <Table.ColumnHeader>Agent</Table.ColumnHeader>
                      <Table.ColumnHeader>Type</Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                      <Table.ColumnHeader>Created By</Table.ColumnHeader>
                      <Table.ColumnHeader>Created</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="center">
                        Conversations
                      </Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="center">
                        Bad
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {runsData?.runs.map((run) => (
                      <Table.Row
                        key={run.id}
                        cursor="pointer"
                        onClick={() =>
                          navigate({ to: `/eval-runs/${run.id}` })
                        }
                      >
                        <Table.Cell fontWeight="medium">
                          {run.name}
                        </Table.Cell>
                        <Table.Cell>
                          <Tag label={run.agent.name} variant="info" />
                        </Table.Cell>
                        <Table.Cell>
                          <Tag
                            label={run.type === 'EndUser' ? 'End User' : 'Internal'}
                            variant={run.type === 'EndUser' ? 'default' : 'default'}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Tag
                            label={run.status}
                            variant={run.status === 'Completed' ? 'success' : 'info'}
                          />
                        </Table.Cell>
                        <Table.Cell fontSize="sm" color="text.subtle">
                          {run.createdBy
                            ? `${run.createdBy.firstName ?? ''} ${run.createdBy.lastName ?? ''}`.trim() ||
                              run.createdBy.email
                            : 'System'}
                        </Table.Cell>
                        <Table.Cell fontSize="sm" color="text.subtle">
                          {formatDate(run.createdAt)}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {run.totalConversations}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {run.needsHelp > 0 ? (
                            <Tag label={String(run.needsHelp)} variant="error" />
                          ) : (
                            <Text fontSize="sm" variant="subtle">
                              0
                            </Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {runsData?.runs.length === 0 && (
                      <Table.Row>
                        <Table.Cell colSpan={8}>
                          <Text variant="subtle" textAlign="center" py={8}>
                            No agent evals yet. Create one to get started.
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
          </Card>
        </Flex>

        {runsData?.pagination && runsData.pagination.total > 0 && (
          <Flex justify="space-between" align="center" px={2}>
            <Text fontSize="sm" variant="subtle">
              {runsData.pagination.total} eval
              {runsData.pagination.total !== 1 ? 's' : ''} total
            </Text>
            <Flex gap={3} align="center">
              <Button
                size="sm"
                disabled={!runsData.pagination.hasPrev}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" variant="subtle">
                Page {runsData.pagination.page} of{' '}
                {runsData.pagination.totalPages}
              </Text>
              <Button
                size="sm"
                disabled={!runsData.pagination.hasNext}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Modal
        open={createDialogOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            setCreateDialogOpen(false);
            setSelectedAgentId('');
            setRunName('');
            setCreateError('');
          }
        }}
        header="New Agent Eval"
        size="md"
        footerProps={{
          submitText: 'Create Eval',
          cancelText: 'Cancel',
          onSubmit: () =>
            createRun({
              agentId: selectedAgentId,
              name: runName || undefined,
            }),
          onCancel: () => setCreateDialogOpen(false),
          loading: isCreating,
          loadingText: 'Creating...',
          disabled: !selectedAgentId || isCreating,
        }}
      >
        <VStack gap={4} align="stretch">
          <Select
            label="Agent"
            required
            placeholder="Select an agent..."
            options={
              agentsData?.agents?.map((agent: { id: string; name: string }) => ({
                label: agent.name,
                value: agent.id,
              })) ?? []
            }
            value={selectedAgentId ? [selectedAgentId] : []}
            onValueChange={(details) =>
              setSelectedAgentId(details.value[0] ?? '')
            }
          />
          <Flex direction="column" gap={2}>
            <Text fontWeight="medium" fontSize="sm">
              Name (optional)
            </Text>
            <TextInput
              placeholder="Auto-generated if empty"
              value={runName}
              onChange={(e) => setRunName(e.target.value)}
            />
          </Flex>
          {createError && (
            <Text fontSize="sm" variant="danger">
              {createError}
            </Text>
          )}
        </VStack>
      </Modal>
    </PageContainer>
  );
}
